import { Check, Copy } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { getDisplayTeamLabel, getHeadlineTypeLabel, type InquiryType } from './contactFormShared';

type PublicContactSuccessCardProps = {
  inquiryType: InquiryType;
  confirmationTeamLabel: string;
  confirmationEmail: string;
  referenceId: string;
  deliveryNotice?: string;
  onReset: () => void;
  onReturnHome?: () => void;
  primaryActionLabel?: string;
  secondaryActionLabel?: string;
};

export default function PublicContactSuccessCard({
  inquiryType,
  confirmationTeamLabel,
  confirmationEmail,
  referenceId,
  deliveryNotice = '',
  onReset,
  onReturnHome,
  primaryActionLabel = 'Send another inquiry',
  secondaryActionLabel = 'Return to main page',
}: PublicContactSuccessCardProps) {
  const [copiedReference, setCopiedReference] = useState(false);
  const copyTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current !== null) {
        window.clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  async function copyReferenceToClipboard() {
    if (!referenceId) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(referenceId);
      } else {
        const element = document.createElement('textarea');
        element.value = referenceId;
        element.setAttribute('readonly', 'true');
        element.style.position = 'absolute';
        element.style.left = '-9999px';
        document.body.appendChild(element);
        element.select();
        document.execCommand('copy');
        document.body.removeChild(element);
      }

      setCopiedReference(true);
      if (copyTimeoutRef.current !== null) {
        window.clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = window.setTimeout(() => {
        setCopiedReference(false);
      }, 2000);
    } catch {
      setCopiedReference(false);
    }
  }

  const typeLabel = getHeadlineTypeLabel(inquiryType);
  const displayTeam = getDisplayTeamLabel(inquiryType, confirmationTeamLabel);

  return (
    <div className="relative overflow-hidden rounded-[40px] border border-[#fce7f0] bg-[#fff1f7] p-8 text-[#0F172A] md:p-10 lg:p-14">
      <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#e12d7d] shadow-md shadow-pink-200">
        <Check className="h-8 w-8 stroke-[3] text-white" />
      </div>

      <div className="mb-4">
        <span className="mb-4 block text-[12px] font-bold uppercase tracking-[0.2em] text-[#e12d7d]">Message sent</span>
        <h2 className="max-w-3xl text-3xl font-bold leading-tight tracking-tight text-slate-900 md:text-4xl">
          {`Your ${typeLabel} was sent to the ${displayTeam} team.`}
        </h2>
      </div>

      <div className="max-w-2xl space-y-6">
        <div className="inline-flex flex-col gap-1 rounded-2xl border border-pink-100 bg-white px-4 py-3 shadow-sm">
          <span className="text-xs font-bold uppercase tracking-[0.24em] text-slate-400">REFERENCE</span>
          <div className="flex items-center gap-3">
            <span className="font-mono text-[20px] font-extrabold text-[#e12d7d]">{referenceId || '—'}</span>
            <button
              type="button"
              onClick={copyReferenceToClipboard}
              className="ml-1 rounded p-1 transition-colors hover:bg-pink-50"
              aria-label="Copy reference ID"
            >
              <Copy className={`h-3.5 w-3.5 ${copiedReference ? 'text-pink-500' : 'text-slate-400'}`} />
            </button>
          </div>
        </div>

        <p className="text-lg leading-relaxed text-slate-600">
          {deliveryNotice ? (
            <>
              <span className="font-medium text-slate-900">{deliveryNotice}</span>{' '}
              Our team typically responds within <span className="font-bold text-slate-900">1–2 business days</span>.
            </>
          ) : (
            <>
              We routed your message to the correct team and sent a confirmation to{' '}
              <span className="font-medium text-slate-900">{confirmationEmail}</span>. Our team typically responds within{' '}
              <span className="font-bold text-slate-900">1–2 business days</span>.
            </>
          )}
        </p>

        <div className="flex flex-col gap-4 pt-4 sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center justify-center rounded-full bg-[#e12d7d] px-10 py-3.5 text-[17px] font-bold text-white shadow-lg shadow-pink-100 transition-all hover:bg-[#c7256d] active:scale-[0.98]"
          >
            {primaryActionLabel}
          </button>

          <button
            type="button"
            onClick={() => {
              if (onReturnHome) {
                onReturnHome();
                return;
              }

              try {
                window.location.assign('/');
              } catch {}
            }}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-8 py-3.5 text-[17px] font-bold text-slate-900 shadow-sm transition-all hover:bg-slate-50 active:scale-[0.98]"
          >
            {secondaryActionLabel}
          </button>
        </div>
      </div>

      <div className="pointer-events-none absolute bottom-0 right-0 opacity-10">
        <svg width="400" height="200" viewBox="0 0 400 200" fill="none" aria-hidden="true">
          <path d="M0 200C150 200 250 0 400 0V200H0Z" fill="#e12d7d" />
        </svg>
      </div>
    </div>
  );
}